import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ExternalLink, BellOff, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeInvalidation } from '../hooks/useRealtimeInvalidation';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  getNotificationColor,
  formatTimeAgo,
  type Notification,
} from '../lib/notificationService';
import { isPushSupported, isLocalhostEnvironment, getCurrentPushSubscription } from '../lib/pushService';

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushActive, setPushActive] = useState<boolean | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const openRef = useRef(open);
  openRef.current = open;

  const handleNotificationUpdate = useCallback(() => {
    loadUnreadCount();
    if (openRef.current) loadNotifications();
  }, []);

  useRealtimeInvalidation('notifications', handleNotificationUpdate);

  useEffect(() => {
    if (!user) return;

    const timer = setTimeout(() => {
      loadUnreadCount();
      checkPushActive();
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, [user]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const loadUnreadCount = async () => {
    try {
      const count = await fetchUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const checkPushActive = async () => {
    if (!isPushSupported() || isLocalhostEnvironment()) { setPushActive(null); return; }
    const sub = await getCurrentPushSubscription();
    setPushActive(!!sub);
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await fetchNotifications(15);
      setNotifications(data);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleMarkRead = async (notif: Notification) => {
    if (!notif.gelezen) {
      await markAsRead(notif.id);
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, gelezen: true, gelezen_op: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    } else if (notif.project_id) {
      navigate(`/project/${notif.project_id}`);
      setOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, gelezen: true, gelezen_op: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="Notificaties"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-0 top-14 mx-2 sm:mx-0 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px] w-auto max-w-[calc(100vw-1rem)] max-h-[70vh] sm:max-h-[520px] bg-surface rounded-xl shadow-2xl border border-theme-border z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border">
            <h3 className="text-sm font-semibold text-foreground">Notificaties</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs text-foreground-tertiary hover:text-foreground-secondary transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Alles gelezen
                </button>
              )}
              <button
                onClick={() => { navigate('/meldingen'); setOpen(false); }}
                className="flex items-center gap-1 text-xs text-brand hover:text-brand-hover font-medium transition-colors"
              >
                Bekijk alles
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-sm text-foreground-tertiary">Laden...</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-12 h-12 bg-surface-tertiary rounded-full flex items-center justify-center mb-3">
                  <Bell className="w-6 h-6 text-foreground-tertiary" />
                </div>
                <p className="text-sm text-foreground-tertiary">Geen notificaties</p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => {
                  const colorClasses = getNotificationColor(notif.type);
                  return (
                    <button
                      key={notif.id}
                      onClick={() => handleMarkRead(notif)}
                      className={`w-full text-left px-4 py-3 border-b border-theme-border hover:bg-surface-secondary transition-colors flex items-start gap-3 ${
                        !notif.gelezen ? 'bg-blue-50/40' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${colorClasses}`}>
                        <Bell className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-tight ${!notif.gelezen ? 'font-semibold text-foreground' : 'font-medium text-foreground-secondary'}`}>
                            {notif.title}
                          </p>
                          {!notif.gelezen && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-foreground-tertiary mt-0.5 line-clamp-2">{notif.body}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {notif.projects && (
                            <span className="text-[10px] font-medium text-foreground-tertiary bg-surface-tertiary px-1.5 py-0.5 rounded">
                              {notif.projects.project_number}
                            </span>
                          )}
                          <span className="text-[10px] text-foreground-tertiary">{formatTimeAgo(notif.created_at)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {pushActive !== null && (
            <div className="px-4 py-2.5 border-t border-theme-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                {pushActive ? (
                  <>
                    <Smartphone className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-[11px] text-green-600 font-medium">Push actief</span>
                  </>
                ) : (
                  <>
                    <BellOff className="w-3.5 h-3.5 text-foreground-tertiary" />
                    <span className="text-[11px] text-foreground-tertiary">Push niet actief</span>
                  </>
                )}
              </div>
              {!pushActive && (
                <button
                  onClick={() => { navigate('/meldingen'); setOpen(false); }}
                  className="text-[11px] text-brand hover:text-brand-hover font-medium transition-colors"
                >
                  Activeren
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
